import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  communityPagesFromPayload,
  fetchCommunityNavigation,
  isCommunityNavVisible,
} from "./communityNavigationApi";
import { fetchOrganizationDirectory } from "../Helper/organizations";
import { fetchCreatorDirectory } from "../Helper/creators";
import { fetchProjectDirectory } from "../Helper/projects";
import CommunityDirectoryGrid from "./CommunityDirectoryGrid";
import ExperienceLandingSections from "../Experience/ExperienceLandingSections";
import ExperienceGate from "../Experience/ExperienceGate";
import "./CommunityPlaceholderPage.css";
import "./CommunityLandingPage.css";

/**
 * Community hub — entry into Social, Organizations, Creators, Projects, Events.
 * Consumes admin-configurable community navigation (no hard-coded menu).
 */
function CommunityLandingPage() {
  const [pages, setPages] = useState([]);
  const [navLabel, setNavLabel] = useState("Community");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [experience, setExperience] = useState(null);

  const [featuredOrgs, setFeaturedOrgs] = useState([]);
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const nav = await fetchCommunityNavigation();
        if (!cancelled) {
          setPages(communityPagesFromPayload(nav));
          setNavLabel((nav && nav.label) || "Community");
          setExperience((nav && nav.experience) || null);
          setLoading(false);
          if (!isCommunityNavVisible(nav)) {
            setError(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(true);
          setLoading(false);
          setExperience(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFeaturedLoading(true);
      const [orgs, creators, projects] = await Promise.all([
        fetchOrganizationDirectory({ featured: true, per_page: 6 }),
        fetchCreatorDirectory({ featured: true, per_page: 6 }),
        fetchProjectDirectory({ featured: true, per_page: 6 }),
      ]);
      if (!cancelled) {
        let orgList = orgs.organizations || [];
        let creatorList = creators.creators || [];
        let projectList = projects.projects || [];
        if (!orgList.length && !orgs.error) {
          const fallback = await fetchOrganizationDirectory({ per_page: 6 });
          orgList = fallback.organizations || [];
        }
        if (!creatorList.length && !creators.error) {
          const fallback = await fetchCreatorDirectory({ per_page: 6 });
          creatorList = fallback.creators || [];
        }
        if (!projectList.length && !projects.error) {
          const fallback = await fetchProjectDirectory({ per_page: 6 });
          projectList = fallback.projects || [];
        }
        setFeaturedOrgs(orgList);
        setFeaturedCreators(creatorList);
        setFeaturedProjects(projectList);
        setFeaturedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ExperienceGate destinationType="social" presentation={experience}>
    <div className="community-page community-landing">
      <Helmet>
        <title>{navLabel}</title>
      </Helmet>

      <ExperienceLandingSections
        scope="community"
        replaceChildren={false}
        secondary={
          <>
      <header
        className="community-page__hero community-landing__hero"
        aria-labelledby="community-landing-title"
      >
        <div className="community-page__shell">
          <p className="community-page__eyebrow">{navLabel}</p>
          <h1 id="community-landing-title" className="community-page__title">
            Connect, discover, and build together
          </h1>
          <p className="community-page__lede">
            Explore ministries, creators, collaborative projects, and community
            gatherings — all in one place.
          </p>
        </div>
      </header>

      <main className="community-page__main">
        <div className="community-page__shell">
          {loading ? (
            <p className="community-page__loading" role="status">
              Loading community…
            </p>
          ) : error ? (
            <div className="community-page__panel" role="alert">
              <h2 className="community-page__panel-title">Unable to load</h2>
              <p className="community-page__panel-body">
                Community navigation could not be loaded right now.
              </p>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <section
                className="community-landing__sections"
                aria-label="Community sections"
              >
                <h2 className="community-page__section-heading">
                  Explore community
                </h2>
                {pages.length === 0 ? (
                  <p className="community-page__panel-body">
                    Community sections will appear here when enabled by your
                    administrator.
                  </p>
                ) : (
                  <ul className="community-landing__nav-grid">
                    {pages.map((page) => {
                      const path = page.path || `/community/${page.slug}`;
                      return (
                        <li key={page.slug || path}>
                          <Link to={path} className="community-landing__nav-card">
                            <span className="community-landing__nav-label">
                              {page.name || page.title || page.slug}
                            </span>
                            {page.description ? (
                              <span className="community-landing__nav-desc">
                                {page.description}
                              </span>
                            ) : null}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section
                className="community-landing__featured"
                aria-label="Featured"
              >
                <h2 className="community-page__section-heading">
                  Featured now
                </h2>
                {featuredLoading ? (
                  <p className="community-page__loading" role="status">
                    Loading highlights…
                  </p>
                ) : (
                  <>
                    {featuredProjects.length > 0 ? (
                      <div className="community-landing__rail">
                        <div className="community-landing__rail-head">
                          <h3>Projects</h3>
                          <Link to="/community/projects">View all</Link>
                        </div>
                        <CommunityDirectoryGrid
                          items={featuredProjects}
                          kindLabel="Project"
                        />
                      </div>
                    ) : null}
                    {featuredOrgs.length > 0 ? (
                      <div className="community-landing__rail">
                        <div className="community-landing__rail-head">
                          <h3>Organizations</h3>
                          <Link to="/community/organizations">View all</Link>
                        </div>
                        <CommunityDirectoryGrid
                          items={featuredOrgs}
                          kindLabel="Organization"
                        />
                      </div>
                    ) : null}
                    {featuredCreators.length > 0 ? (
                      <div className="community-landing__rail">
                        <div className="community-landing__rail-head">
                          <h3>Creators</h3>
                          <Link to="/community/creators">View all</Link>
                        </div>
                        <CommunityDirectoryGrid
                          items={featuredCreators}
                          kindLabel="Creator"
                        />
                      </div>
                    ) : null}
                    {!featuredProjects.length &&
                    !featuredOrgs.length &&
                    !featuredCreators.length ? (
                      <p className="community-page__panel-body">
                        Featured profiles and projects will appear here as
                        communities publish.
                      </p>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </main>
          </>
        }
      />
    </div>
    </ExperienceGate>
  );
}

export default CommunityLandingPage;
